import {EventCard} from '@commercial-helios/ui/src/components/event-card'
import {tokens} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import type {EventCardsSliceFragment} from '@/graphql-types'
import Image from '@helios/shared/components/image'
import PrismicLink from '@helios/shared/components/prismic-link'
import {Section, Layout, BackgroundWrapper} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'

interface Props {
    title: EventCardsSliceFragment['primary']['title']
    eventCtaText: EventCardsSliceFragment['primary']['event_cta_text']
    items: EventCardsSliceFragment['items']
}

export const EventCardsSlice = ({title, eventCtaText, items}: Props) => {
    return (
        <BackgroundWrapper color="muted">
            <StyledSection>
                <StyledText forwardedAs="h2" variant="heading-base">
                    {title}
                </StyledText>
                <Layout variant="two-equal">
                    {items?.map((item) => (
                        <EventCard
                            key={item.title}
                            title={item.title}
                            category={item.category}
                            description={item.description}
                            duration={item.duration}
                            date={item.date}
                            time={item.time}
                            Image={() => <Image image={item.image} />}
                            CTA={(props) => (
                                <PrismicLink
                                    {...props}
                                    {...item.event_cta_link}
                                    onClick={() => {
                                        item.tracking_id &&
                                            track(TrackingEvent.EventCtaActioned, {
                                                action: 'clicked',
                                                value: item.tracking_id,
                                            })
                                    }}
                                >
                                    {eventCtaText}
                                </PrismicLink>
                            )}
                        />
                    ))}
                </Layout>
            </StyledSection>
        </BackgroundWrapper>
    )
}

const StyledSection = styled(Section)`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        gap: ${tokens.spacing60};
    }
`

const StyledText = styled(Text)`
    ${mq.min('sm')} {
        width: 50%;
    }
`

export const eventCardsSliceFragment = graphql`
    fragment EventCardsSlice on PrismicSlicePageDataBodyEventCards {
        __typename
        primary {
            title
            event_cta_text
        }
        items {
            title
            description
            event_cta_link {
                ...PrismicLink
            }
            tracking_id
            category
            duration
            date
            time
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumns)
            }
        }
    }
`
